import React, { useState, useEffect, useMemo } from 'react';
import { Ticket, Volume2, Paperclip } from 'lucide-react';
import { IconArrowForward, IconCircleCheck, IconClock, IconClockPause, IconReportSearch } from '@tabler/icons-react';
import { messageTimeLabel } from '../utils/boardChatUtils';
import { isImageContentType, isAudioContentType } from '../utils/boardChatAttachmentUtils';
import { VoiceDraftPlayer, AttachmentLightbox } from './ComposerAttachments';
import { fetchOrderAttachments } from '../../board/services/boardOrderService';
import './BoardChatPreAtendimentoCard.css';

/**
 * Detecta mensagem de pré-atendimento gerada na abertura de OS pelo Customers.
 * "para pré-atendimento" é opcional (NFC/NFD e textos legados).
 */
export function parsePreAtendimentoCardContent(content) {
  const text = String(content || '')
    .normalize('NFC')
    .trim();
  const match = text.match(
    /Abertura de OS\s+#?([A-Za-z0-9_-]+)(?:\s+para\s+pr[eé][\s-]*atendimento)?/i
  );
  if (!match) return null;

  const osNumber = match[1];
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

  const getField = (prefix) => {
    const found = lines.find((l) => l.toLowerCase().startsWith(prefix.toLowerCase() + ':'));
    return found ? found.slice(prefix.length + 1).trim() : '';
  };

  return {
    osNumber,
    equipamento: getField('Equipamento'),
    defeito: getField('Defeito'),
    acaoTomada: getField('Ação tomada'),
    viuSolucao: getField('Usuário viu sugestão de solução'),
    problema: getField('Problema'),
    solicitante: getField('Solicitante'),
    rawText: text,
  };
}

export function BoardChatPreAtendimentoCard({
  data,
  createdAt,
  osData = null,
  osStatus = 'pending',
  osTrackable = true,
  osDeleted = false,
  onOpenOsMenu,
  onOpenOrderDetails,
  tipProps,
  attachments: initialAttachments = [],
}) {
  const osNumber = data?.osNumber;
  const timeLabel = messageTimeLabel(createdAt);

  const [fetchedAttachments, setFetchedAttachments] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Busca anexos da OS no backend (fotos, áudio de defeito/ação)
  useEffect(() => {
    let active = true;
    if (!osNumber) return;
    fetchOrderAttachments(osNumber)
      .then((res) => {
        if (!active) return;
        if (res?.success && Array.isArray(res.data)) {
          setFetchedAttachments(res.data);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [osNumber]);

  // Mescla anexos vindos da mensagem no chatwoot com anexos consultados da OS
  const allAttachments = useMemo(() => {
    const list = [...(initialAttachments || [])];
    const seenUrls = new Set(list.map((a) => a.url).filter(Boolean));
    const seenKeys = new Set(list.map((a) => a.key || a._key).filter(Boolean));
    const seenNames = new Set(
      list.map((a) => a.filename || a.name || a.originalName).filter(Boolean)
    );

    // Anexos em osData (se houver url direta)
    if (Array.isArray(osData?.anexos)) {
      for (const oa of osData.anexos) {
        if (oa.url && !seenUrls.has(oa.url)) {
          seenUrls.add(oa.url);
          list.push({
            url: oa.url,
            filename: oa.nome || oa.filename || 'anexo',
            contentType: oa.contentType || '',
            key: oa.key,
          });
        }
      }
    }

    // Anexos retornados pelo endpoint da OS
    for (const f of fetchedAttachments) {
      if (f.url && seenUrls.has(f.url)) continue;
      if (f.key && seenKeys.has(f.key)) continue;
      if (f._key && seenKeys.has(f._key)) continue;
      const fn = f.originalName || f.name;
      if (fn && seenNames.has(fn)) continue;

      if (f.url) seenUrls.add(f.url);
      if (f.key) seenKeys.add(f.key);
      if (fn) seenNames.add(fn);

      list.push({
        url: f.url,
        filename: fn || 'anexo',
        contentType: f.isImage ? 'image/jpeg' : (f.isAudio ? 'audio/webm' : ''),
        isImage: f.isImage,
        isAudio: f.isAudio,
        source: f.source || null,
        originalName: f.originalName || f.name || null,
      });
    }

    return list;
  }, [initialAttachments, fetchedAttachments, osData]);

  // Separação em fotos, áudios e outros arquivos
  const photos = useMemo(() => {
    return allAttachments.filter((a) => {
      if (a.isImage) return true;
      const ct = String(a.contentType || '').toLowerCase();
      if (ct.startsWith('image/')) return true;
      const fn = String(a.filename || a.originalName || a.name || a.url || '').toLowerCase();
      return /\.(jpe?g|png|gif|webp|bmp|heic|svg)$/i.test(fn.split('?')[0]);
    });
  }, [allAttachments]);

  const audios = useMemo(() => {
    return allAttachments.filter((a) => {
      if (a.isAudio) return true;
      const ct = String(a.contentType || '').toLowerCase();
      if (ct.startsWith('audio/')) return true;
      const fn = String(a.filename || a.originalName || a.name || a.url || '').toLowerCase();
      return /\.(webm|ogg|mp3|m4a|wav|opus|aac)$/i.test(fn.split('?')[0]);
    });
  }, [allAttachments]);

  const otherFiles = useMemo(() => {
    return allAttachments.filter(
      (a) => !photos.includes(a) && !audios.includes(a)
    );
  }, [allAttachments, photos, audios]);

  // Associação de áudio específico para cada observação
  const acaoAudio = useMemo(() => {
    return (
      audios.find((a) => {
        const n = String(a.filename || a.originalName || a.name || '').toLowerCase();
        return n.includes('audio_acao') || n.includes('audio-acao') || a.source === 'audio-acao';
      }) || null
    );
  }, [audios]);

  const problemaAudio = useMemo(() => {
    return (
      audios.find((a) => {
        const n = String(a.filename || a.originalName || a.name || '').toLowerCase();
        return (
          n.includes('audio_defeito') ||
          n.includes('audio-defeito') ||
          n.includes('audio_problema') ||
          a.source === 'audio-defeito'
        );
      }) || null
    );
  }, [audios]);

  const otherAudios = useMemo(() => {
    return audios.filter((a) => a !== acaoAudio && a !== problemaAudio);
  }, [audios, acaoAudio, problemaAudio]);

  const equipText = String(
    data?.equipamento ||
      (osData?.equipamento
        ? [
            osData.equipamento.modelo,
            osData.equipamento.num_serie ? `S/N: ${osData.equipamento.num_serie}` : null,
            osData.equipamento.patrimonio ? `Tag: ${osData.equipamento.patrimonio}` : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : '')
  ).replace(/Patrim[oô]nio/gi, 'Tag');

  const defeitoText = osData?.defeito?.descricao || data?.defeito || '';
  const acaoText = osData?.acaoTomada || data?.acaoTomada || '';
  const viuSolucaoText = osData?.viuSolucao || data?.viuSolucao || '';
  const problemaText = osData?.problema || data?.problema || '';
  const showProblema = problemaText && problemaText.toLowerCase() !== 'não informado';

  const solicitanteText =
    data?.solicitante ||
    (osData?.solicitante?.nome
      ? `${osData.solicitante.nome}${
          osData.solicitante.telefone ? ` | Contato: ${osData.solicitante.telefone}` : ''
        }`
      : '');

  const handleOpenOs = (e) => {
    e.stopPropagation();
    if (!osNumber || osDeleted) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (typeof onOpenOsMenu === 'function') {
      onOpenOsMenu({
        osId: osNumber,
        status: String(osStatus || 'pending').toLowerCase(),
        osData: osData || null,
        trackable: osTrackable !== false,
        rect,
      });
      return;
    }
    if (typeof onOpenOrderDetails === 'function') {
      onOpenOrderDetails(osNumber, { isPreAtendimento: true });
    }
  };

  const statusNorm = String(osStatus || 'pending').toLowerCase();
  const statusLabel = osDeleted
    ? 'Excluída'
    : statusNorm === 'forwarded'
      ? 'Encaminhada'
      : statusNorm === 'closed'
        ? 'Fechada'
        : statusNorm === 'snoozed'
          ? 'Adiada'
          : 'Pendente';
  const osTip = {
    title: `OS ${osNumber}`,
    rows: [
      { label: 'Status', value: statusLabel },
      !osDeleted && osTrackable === false
        ? { label: 'Controle', value: 'Sem vínculo rastreável' }
        : null,
    ].filter(Boolean),
  };

  const osBtnClass = [
    'bch-pre-os-btn',
    osDeleted ? 'bch-pre-os-btn--deleted' : '',
    !osDeleted && osTrackable === false ? 'bch-pre-os-btn--untracked' : '',
    !osDeleted && statusNorm === 'forwarded' ? 'bch-pre-os-btn--forwarded' : '',
    !osDeleted && statusNorm === 'closed' ? 'bch-pre-os-btn--closed' : '',
    !osDeleted && statusNorm === 'snoozed' ? 'bch-pre-os-btn--snoozed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const osBtnInner = (
    <>
      <Ticket size={12} strokeWidth={2} />
      <span>OS {osNumber}</span>
      {osDeleted ? (
        <span className="bch-pre-os-deleted-label">Excluída</span>
      ) : statusNorm === 'forwarded' ? (
        <IconArrowForward size={12} stroke={2} className="bch-pre-os-status-ico" aria-hidden />
      ) : statusNorm === 'closed' ? (
        <IconCircleCheck size={12} stroke={2} className="bch-pre-os-status-ico" aria-hidden />
      ) : statusNorm === 'snoozed' ? (
        <IconClockPause size={12} stroke={2} className="bch-pre-os-status-ico" aria-hidden />
      ) : (
        <IconReportSearch size={12} stroke={2} className="bch-pre-os-btn-icon" />
      )}
    </>
  );

  return (
    <div className={`bch-pre-card${osDeleted ? ' bch-pre-card--deleted' : ''}`}>
      <div className="bch-pre-card-head">
        <div className="bch-pre-card-head-left">
          {osNumber ? (
            osDeleted ? (
              <span
                className={osBtnClass}
                aria-label={`OS ${osNumber} · Excluída`}
                {...(typeof tipProps === 'function' ? tipProps(osTip) : {})}
              >
                {osBtnInner}
              </span>
            ) : (
              <button
                type="button"
                className={osBtnClass}
                onClick={handleOpenOs}
                aria-label={`OS ${osNumber} · ${statusLabel}`}
                aria-haspopup="menu"
                {...(typeof tipProps === 'function' ? tipProps(osTip) : {})}
              >
                {osBtnInner}
              </button>
            )
          ) : null}
        </div>
        <div className="bch-pre-card-head-right">
          {timeLabel ? (
            <span className="bch-pre-time">
              <IconClock size={11} stroke={1.8} />
              <span>{timeLabel}</span>
            </span>
          ) : null}
          <span className="bch-pre-lock-tag" title="Visível apenas para a equipe">
            Nota interna
          </span>
        </div>
      </div>

      <div className="bch-pre-card-body">
        {equipText ? (
          <div className="bch-pre-row">
            <span className="bch-pre-label">Equipamento:</span>
            <span className="bch-pre-val bch-pre-val--bold">{equipText}</span>
          </div>
        ) : null}

        {defeitoText ? (
          <div className="bch-pre-row">
            <span className="bch-pre-label">Defeito:</span>
            <span className="bch-pre-val">
              <span className="bch-pre-val-defect">{defeitoText}</span>
            </span>
          </div>
        ) : null}

        {(acaoText && acaoText.toLowerCase() !== 'não informada') || acaoAudio ? (
          <div className="bch-pre-row bch-pre-row--acao">
            <span className="bch-pre-label">Ação tomada:</span>
            <div className="bch-pre-val">
              {acaoText && acaoText.toLowerCase() !== 'não informada' ? (
                <span>{acaoText}</span>
              ) : null}
              {acaoAudio ? (
                <div className="bch-pre-audio-player-wrap">
                  <VoiceDraftPlayer src={acaoAudio.url} />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {viuSolucaoText ? (
          <div className="bch-pre-row">
            <span className="bch-pre-label">Viu sugestão:</span>
            <span className="bch-pre-val">{viuSolucaoText}</span>
          </div>
        ) : null}

        {showProblema || problemaAudio ? (
          <div className="bch-pre-row bch-pre-row--problem">
            <span className="bch-pre-label">Problema:</span>
            <div className="bch-pre-val bch-pre-val--quote">
              {showProblema ? <div>{problemaText}</div> : null}
              {problemaAudio ? (
                <div className="bch-pre-audio-player-wrap">
                  <VoiceDraftPlayer src={problemaAudio.url} />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {otherAudios.map((audio, idx) => (
          <div key={audio.url || idx} className="bch-pre-row bch-pre-row--audio">
            <span className="bch-pre-label">
              <Volume2 size={12} strokeWidth={2} className="bch-pre-audio-label-icon" />
              Áudio:
            </span>
            <div className="bch-pre-val">
              <div className="bch-pre-audio-player-wrap">
                <VoiceDraftPlayer src={audio.url} />
              </div>
            </div>
          </div>
        ))}

        {photos.length > 0 ? (
          <div className="bch-pre-row bch-pre-row--photos">
            <span className="bch-pre-label">Fotos ({photos.length}):</span>
            <div className="bch-pre-val">
              <div className="bch-pre-photos-grid">
                {photos.map((item, idx) => (
                  <button
                    key={item.url || idx}
                    type="button"
                    className="bch-pre-photo-thumb"
                    onClick={() => setLightboxIndex(idx)}
                    title={item.filename || `Foto ${idx + 1} (clique para ampliar)`}
                    aria-label={`Ver foto ${idx + 1}`}
                  >
                    <img src={item.url} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {otherFiles.length > 0 ? (
          <div className="bch-pre-row bch-pre-row--files">
            <span className="bch-pre-label">Anexos:</span>
            <div className="bch-pre-val bch-pre-files-list">
              {otherFiles.map((f, idx) => (
                <a
                  key={f.url || idx}
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bch-pre-file-chip"
                  title={f.filename}
                >
                  <Paperclip size={11} strokeWidth={2} />
                  <span className="bch-pre-file-name">{f.filename}</span>
                </a>
              ))}
            </div>
          </div>
        ) : null}

        {solicitanteText ? (
          <div className="bch-pre-row bch-pre-row--solicitante">
            <span className="bch-pre-label">Solicitante:</span>
            <span className="bch-pre-val">{solicitanteText}</span>
          </div>
        ) : null}
      </div>

      {lightboxIndex != null && photos.length > 0 ? (
        <AttachmentLightbox
          items={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      ) : null}
    </div>
  );
}
